import React from 'react';
import { LucideIcon } from 'lucide-react';

interface KpiCardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  isHero?: boolean;
  change?: string;
  trend?: 'up' | 'down';
}

export function KpiCard({ label, value, icon: Icon, isHero = false, change, trend }: KpiCardProps) {
  return (
    <div
      className={`relative group bg-white border rounded-2xl p-6 transition-all duration-300 hover:-translate-y-0.5 ${
        isHero
          ? 'border-brand/40 shadow-[0_1px_3px_rgba(0,0,0,0.02),0_12px_36px_-6px_color-mix(in_srgb,_var(--brand-primary)_12%,_rgba(0,0,0,0.03))] ring-1 ring-brand/10'
          : 'border-slate-200/50 shadow-[0_1px_3px_rgba(0,0,0,0.01),0_10px_40px_-10px_rgba(0,0,0,0.04)] hover:shadow-[0_1px_3px_rgba(0,0,0,0.01),0_15px_45px_-8px_rgba(0,0,0,0.08)] hover:border-slate-300/60'
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">{label}</p>
          <p className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-none mt-1">
            {value}
          </p>
        </div>

        {Icon && (
          <div
            className={`p-3 rounded-xl border shrink-0 transition-all duration-300 group-hover:scale-105 ${
              isHero
                ? 'bg-brand text-white border-brand/20 shadow-md shadow-brand/15'
                : 'bg-brand/10 text-brand border-brand/10'
            }`}
          >
            <Icon className="h-5 w-5" strokeWidth={isHero ? 2.5 : 2} />
          </div>
        )}
      </div>

      {(change || trend) && (
        <div className="mt-4 flex items-center gap-1.5 text-xs min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" title={typeof change === 'string' ? change : undefined}>
          {trend === 'up' && <span className="text-emerald-600 font-bold shrink-0">↑</span>}
          {trend === 'down' && <span className="text-rose-600 font-bold shrink-0">↓</span>}
          <span className={`font-semibold truncate min-w-0 ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-rose-600' : 'text-slate-500'}`}>
            {change}
          </span>
        </div>
      )}
    </div>
  );
}

interface StatusBadgeProps {
  label: string;
  type?: 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand';
}

export function StatusBadge({ label, type = 'neutral' }: StatusBadgeProps) {
  const styles = {
    brand: 'bg-brand/10 text-brand border-brand/20',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-150/40',
    warning: 'bg-amber-50 text-amber-700 border-amber-150/40',
    danger: 'bg-rose-50 text-rose-700 border-rose-150/40',
    info: 'bg-blue-50 text-blue-700 border-blue-150/40',
    neutral: 'bg-slate-50 text-slate-600 border-slate-200/50',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${styles[type]}`}>
      {label}
    </span>
  );
}
